import { RenderingManager } from 'rendering-manager';
import { LayerInterface } from 'types/layer';

export class FlipAnimation implements AnimationInterface {
	private initialState: {
		options: BaseDrawOptions;
		children: Record<string, any>;
	} = { options: { scale: 0, x: 0, y: 0, w: 0, h: 0 }, children: {} };
	private layer: LayerInterface;
	private renderingManager: RenderingManager;
	private context: CanvasRenderingContext2D;
	private scale = 1;
	private zoom = 1;
	private direction = -1;
	private stopAnimation = false;
	private shadowOffsetX = 0;
	private shadowOffsetY = 0;
	private animationOverCallbacks: Array<() => void> = [];
	private onFlipInMiddleCallback = () => {};
	private isFlipInMiddleCallbackCalled = false;
	private animationInProgress = false;

	constructor(layer: LayerInterface, renderingManager: RenderingManager) {
		this.layer = layer;
		this.renderingManager = renderingManager;
		this.context = renderingManager.getContext();
		const children = this.layer.getChildren();

		this.initialState.options = layer.getOptions();
		for (const child of children) {
			const options = child.getOptions();
			if (options.shadowOffsetX) {
				this.shadowOffsetX = options.shadowOffsetX;
			}

			if (options.shadowOffsetY) {
				this.shadowOffsetY = options.shadowOffsetY;
			}
			this.initialState.children[child.getType()] = options;
		}
	}

	onAnimationOver(callBack: () => void) {
		this.animationOverCallbacks.push(callBack);
	}

	onFlipInMiddle(callBack: () => void) {
		this.onFlipInMiddleCallback = callBack;
	}

	animation() {
		const { options: initialOptions } = this.initialState;

		if (this.scale !== 0) {
			this.renderingManager?.reDrawSync({ exceptLayer: this.layer });
		}
		const [w, h] = this.layer.getWidthHeight();

		this.context.save();
		this.context.translate(
			initialOptions.x + w / 2,
			initialOptions.y + h / 2
		);
		this.context.scale(this.scale, this.zoom);

		this.layer.setXY(-w / 2, -h / 2);

		const activeLayerChildren = this.layer.getChildren();

		for (const activeLayerChild of activeLayerChildren) {
			if (this.direction > 0 && !this.isFlipInMiddleCallbackCalled) {
				this.onFlipInMiddleCallback();
				this.isFlipInMiddleCallbackCalled = true;
			}

			const options = activeLayerChild?.getOptions();

			if (options.shadowOffsetX) {
				this.shadowOffsetX += -5 * this.direction;
				activeLayerChild.setOptions({
					shadowOffsetX: this.shadowOffsetX,
				});
			}

			if (options.shadowOffsetY) {
				this.shadowOffsetY += -5 * this.direction;
				activeLayerChild.setOptions({
					shadowOffsetY: this.shadowOffsetY,
				});
			}
			activeLayerChild?.setXY(-options.w / 2, -options.h / 2);
		}

		if (this.scale !== 0) {
			this.renderingManager?.draw(this.layer);
		}
		//this.context.setTransform(2, 0,0, 2, 0, 0);
		this.context.restore();

		this.scale = Number((this.scale + 0.1 * this.direction).toFixed(3));
		this.zoom += -0.05 * this.direction;

		if (this.scale <= 0) {
			this.direction = 1;
		}

		if (this.zoom < 1) {
			this.zoom = 1;
		}

		if (this.scale > 1 && this.direction > 0) {
			this.stopAnimation = true;
			this.layer.setShouldBeRendered(true);
			this.layer.setAnimationStatus('stopped');
			this.layer.restore();
			this.animationOverCallbacks.forEach((callback) => {
				callback();
			});
			this.renderingManager.reDraw();
		}
	}

	public getStatus(): string {
		return this.layer.getAnimationStatus();
	}

	public animate(firstIteration = true) {
		if (firstIteration) {
			this.layer.setShouldBeRendered(false);
			this.layer.setAnimationStatus('running');
			this.animationInProgress = true;
			this.layer.save();

			window.requestAnimationFrame(() => {
				this.animate(false);
			});
		} else {
			if (!this.stopAnimation) {
				this.animation();

				window.requestAnimationFrame(() => {
					this.animate(false);
				});
			}
		}
	}
}
